import type { SceneObject } from '../SceneObject';
// Type-only import of Properties for the typed getBehavior('Properties')
// string overload. A VALUE import would cycle: properties.ts imports
// PropertyResolver from this same core/canopy folder, so
// core/canopy -> behaviors/properties -> core/canopy. The string overload
// (typed as Properties | null) sidesteps that — same pattern core/SceneObject.ts
// uses for the same reason.
import type { Properties } from '../../behaviors/properties';

// ---------------------------------------------------------------------------
// State SCOPE — the runtime fan-out of a scoped `trace/setState`.
//
// `trace/setState(target, s)` sets the state slot of `target` AND of every
// participating descendant within the target's scope, resolved against the live
// scene when the node executes. State groups are thereby a first-class runtime
// concept: one node, no baked per-member chain, no editor-metadata marker.
//
// This module is the single source of truth for that walk. It lives in core (not
// editor/) so the runtime setState block and the editor's live preview share ONE
// implementation and cannot disagree. It reads only authored group data that
// already exists — `Properties.stateGroup` (boundary) and `Properties`'
// declared states (participation) — so membership is never cached and never
// stale, including under script-driven topology changes.
//
// Normative spec: docs/canopy/STATE_SCOPE_SPEC.md §2–§3.
// ---------------------------------------------------------------------------

/** True when `obj` owns its state group — a scope boundary (R3). */
export function isScopeBoundary(obj: SceneObject): boolean {
    return obj.getBehavior('Properties')?.isStateGroupOwner() ?? false;
}

/**
 * Every descendant of `owner` within its scope, in pre-order DFS, pruned at
 * nested `stateGroup` boundaries (a boundary and its whole subtree are
 * excluded). Excludes `owner` itself, and does NOT apply any participation
 * filter — this is the raw scope membership shared by the runtime fan-out and
 * every editor coordination walk (fan-out, ref-heal, thumbnail signatures), so
 * they cannot disagree about what belongs to a group. `owner` is never treated
 * as its own boundary even if it carries the marker.
 */
export function scopedDescendants(owner: SceneObject): SceneObject[] {
    const result: SceneObject[] = [];
    collectDescendants(owner.getChildren(), result);
    return result;
}

function collectDescendants(nodes: SceneObject[], out: SceneObject[]): void {
    for (const node of nodes) {
        if (isScopeBoundary(node)) continue; // R3 — prune node + subtree
        out.push(node);
        collectDescendants(node.getChildren(), out);
    }
}

/**
 * Whether `props` participates in state `s` (§2).
 * - Named state (`s` a string): the object's own Properties declares `s`.
 * - Base (`s === undefined`): the object declares at least one state.
 */
function participates(props: Properties, s: string | undefined): boolean {
    const names = props.getStateNames();
    return s === undefined ? names.length > 0 : names.includes(s);
}

/**
 * The objects a scoped `trace/setState(target, s)` writes, in R5 order
 * (pre-order DFS, target first). Pure — performs no writes.
 *
 * - **R1** `target` is ALWAYS the first element, independent of the
 *   participation filter (its slot is the scope's tracking slot). Its own write
 *   is still subject to the per-object rules in {@link applyScopedState}.
 * - **R2** a strict descendant is included iff it participates in `s`.
 * - **R3** a strict descendant carrying `stateGroup: true` is pruned together
 *   with its whole subtree. Only `stateGroup` prunes — an object with no
 *   Properties does NOT prune; the walk continues through it (so e.g. a plain
 *   grouping transform between the target and a participating leaf is
 *   transparent).
 *
 * `s === undefined` means Base.
 */
export function collectScopedStateTargets(
    target: SceneObject,
    s: string | undefined,
): SceneObject[] {
    const result: SceneObject[] = [target]; // R1 — target first, unconditional
    for (const d of scopedDescendants(target)) {
        // R2 — a strict descendant is included iff it participates in s. Objects
        // with no Properties are already skipped here (participation needs a
        // Properties), but scopedDescendants still walked THROUGH them (R3: only
        // stateGroup prunes), so a participating leaf under a plain transform is
        // reached. Pre-order DFS from scopedDescendants preserves R5 order.
        const props = d.getBehavior('Properties');
        if (props && participates(props, s)) result.push(d);
    }
    return result;
}

/**
 * Execute a scoped `trace/setState(target, s)`: write the state slot of `target`
 * and of every participating descendant within its scope, in R5 order.
 * `s === undefined` means Base.
 *
 * Per-object write rules (R6) are the existing `Properties.setState` semantics
 * and live there: a named state a member does not declare is a silent no-op
 * (dangling-reference safety net); Base is always accepted. Idempotence (R7 —
 * writing a slot to its current value is a strict no-op) is enforced by
 * `PropertyResolver.setState`. A target with no Properties skips its own write
 * (the existing warning fires at the call site) — its descendants are still
 * processed, which is why callers should resolve the target's own Properties
 * themselves rather than relying on this function to warn.
 */
export function applyScopedState(target: SceneObject, s: string | undefined): void {
    for (const obj of collectScopedStateTargets(target, s)) {
        obj.getBehavior('Properties')?.setState(s);
    }
}
